import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2, Plus, Trash2, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { recipeService } from '../../../services/procesos/recipeService';

const UNITS = ['ºC', 'bars', 'N', 'Nm', '%', 'rpm', 'mpm', '_'];
const DATA_TYPES = ['float', 'int', 'bool', 'string', 'enum'];

const id = (a) => a.ID ?? a.id;
const parentId = (a) => a.ParentID ?? a.parentID ?? a.parent_id;
// Backend envía tech_code (snake_case); por si hay normalización a camelCase también techCode
const techCode = (a) => a.tech_code ?? a.TechCode ?? a.techCode ?? a.ID ?? a.id;
const label = (a) => {
  const code = a.Code ?? a.code ?? '';
  const tc = techCode(a);
  return code ? `${code} (${tc})` : `ID ${id(a)}`;
};

/** Construye árbol de activos: nodos con { asset, children } donde children son hijos en la línea */
function buildAssetTree(assets, lineId) {
  if (!assets?.length || lineId == null) return [];
  const byParent = new Map();
  for (const a of assets) {
    const pid = parentId(a);
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(a);
  }
  const build = (asset) => ({
    asset,
    children: (byParent.get(id(asset)) || []).map(build),
  });
  return (byParent.get(lineId) || []).map(build);
}

/** Desplegable tipo árbol para elegir un activo de la línea */
function AssetTreeSelect({ tree, value, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const selectedLabel = useMemo(() => {
    if (!value || !tree?.length) return '';
    const find = (nodes) => {
      for (const node of nodes) {
        if (techCode(node.asset) === value) return label(node.asset);
        const l = find(node.children);
        if (l) return l;
      }
      return null;
    };
    return find(tree) || '';
  }, [tree, value]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelect = (node) => {
    onChange(techCode(node.asset));
    setOpen(false);
  };

  const renderNode = (node, depth = 0, keyPrefix = '') => {
    const key = `${keyPrefix}-${id(node.asset)}`;
    const hasChildren = node.children?.length > 0;
    const isExp = hasChildren && expanded.has(key);
    const tc = techCode(node.asset);
    const isSelected = Number(value) === Number(tc);

    return (
      <div key={key}>
        <div
          role="button"
          tabIndex={0}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => handleSelect(node)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelect(node);
            }
          }}
          className={`
            flex items-center gap-1 py-2 px-3 text-sm cursor-pointer rounded-lg
            hover:bg-gray-700/80 transition-colors
            ${isSelected ? 'bg-gray-700 text-white' : 'text-gray-200'}
          `}
        >
          <span
            role="button"
            tabIndex={0}
            className="w-4 flex items-center justify-center text-gray-400 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggle(key);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (hasChildren) toggle(key);
              }
            }}
          >
            {hasChildren ? (
              isExp ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )
            ) : null}
          </span>
          <span className="flex-1 truncate">{label(node.asset)}</span>
        </div>
        {hasChildren && isExp && (
          <div>{node.children.map((child, i) => renderNode(child, depth + 1, `${key}-${i}`))}</div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500/60 transition-shadow"
      >
        <span className="truncate">{selectedLabel || 'Select asset'}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            role="button"
            tabIndex={-1}
            aria-label="Close"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          />
          <div className="absolute z-20 mt-1.5 left-0 right-0 max-h-56 overflow-auto bg-gray-800 border border-gray-700/80 rounded-xl shadow-xl py-1">
            {tree.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No assets in line</div>
            ) : (
              tree.map((node, i) => renderNode(node, 0, `r-${i}`))
            )}
          </div>
        </>
      )}
    </div>
  );
}

const emptyNewParam = () => ({
  asset_tech_code: 0,
  name: '',
  unit: 'ºC',
  data_type: 'float',
  plc_address: '',
  is_mandatory: true,
  options: [],
});

export function TemplateFormModal({
  mode,
  templateId,
  lineAssetTechCode,
  lineAssets = [],
  lineId = null,
  onClose,
  onSave,
  isSaving,
  error,
}) {
  const assetTree = useMemo(() => buildAssetTree(lineAssets, lineId), [lineAssets, lineId]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('');
  const [parameterIds, setParameterIds] = useState([]);
  const [newParameters, setNewParameters] = useState([emptyNewParam()]);
  const [formError, setFormError] = useState(null);

  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: ['recipe-template', templateId],
    queryFn: () => recipeService.getTemplate(templateId),
    enabled: mode === 'edit' && !!templateId,
    retry: 1,
  });

  useEffect(() => {
    if (mode === 'edit' && template) {
      setName(template.name ?? '');
      setDescription(template.description ?? '');
      setVersion(template.version ?? '');
      const ids = (template.parameters ?? []).map((p) => p.id);
      setParameterIds(ids);
      if (ids.length === 0) {
        setNewParameters([emptyNewParam()]);
      } else {
        setNewParameters([emptyNewParam()]);
      }
    }
    if (mode === 'create') {
      setName('');
      setDescription('');
      setVersion('');
      setParameterIds([]);
      setNewParameters([emptyNewParam()]);
    }
    setFormError(null);
  }, [mode, template, templateId]);

  const addNewParameter = () => {
    setNewParameters((prev) => [...prev, emptyNewParam()]);
  };

  const removeNewParameter = (index) => {
    setNewParameters((prev) => prev.filter((_, i) => i !== index));
  };

  const updateNewParameter = (index, field, value) => {
    setNewParameters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const setOptionsForParam = (index, optionsArray) => {
    setNewParameters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], options: optionsArray };
      return next;
    });
  };

  /** Quitar un parámetro existente (deja de enviarse en parameter_ids) */
  const removeExistingParameter = (paramId) => {
    setParameterIds((prev) => prev.filter((id) => id !== paramId));
  };

  /** Editar: quitar de actuales y añadir como nuevo con los datos prellenados */
  const editExistingParameter = (param) => {
    const idToRemove = param.id ?? param.ID;
    setParameterIds((prev) => prev.filter((id) => id !== idToRemove));
    let resolvedTechCode = param.asset_tech_code ?? param.assetTechCode ?? 0;
    if (resolvedTechCode === 0 && (param.asset_id ?? param.assetID) != null && lineAssets?.length) {
      const aid = param.asset_id ?? param.assetID;
      const asset = lineAssets.find((a) => Number(id(a)) === Number(aid));
      if (asset) resolvedTechCode = Number(techCode(asset)) || 0;
    }
    const row = {
      asset_tech_code: resolvedTechCode,
      name: param.name ?? '',
      unit: param.unit ?? 'ºC',
      data_type: param.data_type ?? param.dataType ?? 'float',
      plc_address: param.plc_address ?? param.plcAddress ?? '',
      is_mandatory: true,
      options: Array.isArray(param.options) ? param.options : [],
    };
    setNewParameters((prev) => [...prev, row]);
  };

  /** Parámetros actuales que seguimos conservando (están en parameterIds) */
  const currentParameters =
    mode === 'edit' && template?.parameters?.length
      ? (template.parameters ?? []).filter((p) =>
          parameterIds.includes(p.id ?? p.ID)
        )
      : [];

  const validate = () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('El nombre es obligatorio.');
      return false;
    }
    const validNew = newParameters.filter(
      (p) => p.name.trim() && (p.asset_tech_code > 0 || mode === 'edit')
    );
    if (mode === 'create' && validNew.length === 0) {
      setFormError('Añade al menos un parámetro nuevo con nombre y activo seleccionado.');
      return false;
    }
    for (let i = 0; i < newParameters.length; i++) {
      const p = newParameters[i];
      if (!p.name.trim()) continue;
      if (p.data_type === 'enum' && (!p.options || p.options.length === 0)) {
        setFormError(`Parámetro "${p.name}": para tipo enum se requiere al menos una opción.`);
        return false;
      }
      if (!UNITS.includes(p.unit)) {
        setFormError(`Parámetro "${p.name}": unidad no válida.`);
        return false;
      }
      if (!DATA_TYPES.includes(p.data_type)) {
        setFormError(`Parámetro "${p.name}": tipo de dato no válido.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const newParamsFiltered = newParameters.filter(
      (p) => p.name.trim() && (p.asset_tech_code > 0 || mode === 'edit')
    );
    const newParamsMapped = newParamsFiltered.map((p) => ({
      asset_tech_code: Number(p.asset_tech_code) || 0,
      name: p.name.trim(),
      unit: p.unit,
      data_type: p.data_type,
      plc_address: p.plc_address ?? '',
      is_mandatory: true,
      options: p.data_type === 'enum' ? (Array.isArray(p.options) ? p.options : []) : undefined,
    }));
    const newParamsPayload =
      mode === 'create'
        ? newParamsMapped.filter((p) => p.asset_tech_code > 0)
        : newParamsMapped;

    if (mode === 'create') {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        version: version.trim(),
        line_asset_tech_code: lineAssetTechCode,
        parameter_ids: parameterIds.length > 0 ? parameterIds : undefined,
        new_parameters: newParamsPayload.length > 0 ? newParamsPayload : undefined,
      });
    } else {
      await onSave({
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        version: version.trim() || undefined,
        parameter_ids: parameterIds.length > 0 ? parameterIds : undefined,
        new_parameters: newParamsPayload.length > 0 ? newParamsPayload : undefined,
      });
    }
  };

  const displayError = formError || (error?.response?.data?.error) || error?.message;

  if (mode === 'edit' && !!templateId && loadingTemplate) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="bg-gray-800/95 rounded-2xl p-6 shadow-2xl shadow-black/50 max-w-lg w-full mx-4 border border-gray-700/80">
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading template…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-800/95 rounded-2xl shadow-2xl shadow-black/50 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-700/80">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/80 bg-gray-900/40">
          <h2 className="text-lg font-semibold text-white">
            {mode === 'create' ? 'New template' : 'Edit template'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 transition-shadow"
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 transition-shadow"
                maxLength={500}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Version</label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 transition-shadow"
                maxLength={50}
              />
            </div>

            {mode === 'edit' && (currentParameters.length > 0 || (template?.parameters?.length > 0 && currentParameters.length === 0)) && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Current parameters</label>
                {currentParameters.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">None kept. Add new ones below.</p>
                ) : (
                  <div className="space-y-2">
                    {currentParameters.map((param) => {
                      const pid = param.id ?? param.ID;
                      const techCode = param.asset_tech_code ?? param.assetTechCode ?? '-';
                      return (
                        <div
                          key={pid}
                          className="flex items-center justify-between gap-2 py-3 px-4 bg-gray-900/60 rounded-xl border border-gray-700/80 hover:border-gray-600/80 transition-colors"
                        >
                          <div className="flex-1 min-w-0 text-sm text-gray-200">
                            <span className="font-medium">{param.name ?? '-'}</span>
                            <span className="text-gray-500 ml-2">
                              {param.unit ?? ''} · {param.data_type ?? param.dataType ?? ''} · asset {techCode}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => editExistingParameter(param)}
                              className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                              title="Edit (move to new and modify)"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeExistingParameter(pid)}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Remove from template"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-400">New parameters</label>
                <button
                  type="button"
                  onClick={addNewParameter}
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-blue-500/10 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {newParameters.map((param, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-900/60 rounded-xl border border-gray-700/80 space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeNewParameter(index)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Asset (line)</label>
                        <AssetTreeSelect
                          tree={assetTree}
                          value={param.asset_tech_code || ''}
                          onChange={(v) => updateNewParameter(index, 'asset_tech_code', v ? Number(v) : 0)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Name *</label>
                        <input
                          type="text"
                          value={param.name}
                          onChange={(e) => updateNewParameter(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60 transition-shadow"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit</label>
                        <select
                          value={param.unit}
                          onChange={(e) => updateNewParameter(index, 'unit', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                        >
                          {UNITS.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Data type</label>
                        <select
                          value={param.data_type}
                          onChange={(e) => updateNewParameter(index, 'data_type', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                        >
                          {DATA_TYPES.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">PLC address</label>
                      <input
                        type="text"
                        value={param.plc_address ?? ''}
                        onChange={(e) => updateNewParameter(index, 'plc_address', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60 transition-shadow"
                      />
                    </div>
                    {param.data_type === 'enum' && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Options (one per line)</label>
                        <textarea
                          value={(param.options || []).join('\n')}
                          onChange={(e) =>
                            setOptionsForParam(
                              index,
                              e.target.value
                                .split('\n')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            )
                          }
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60 placeholder-gray-500 transition-shadow"
                          placeholder="option1&#10;option2"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {displayError && (
              <p className="text-sm text-red-400">{displayError}</p>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-700/80 bg-gray-900/30 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-600 text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
